import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog'
import { Formik } from 'formik'
import * as Yup from 'yup'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { Label } from './ui/label'
import { cn } from '@/lib/utils'
import { Loader2, Send, CheckCircle2, Building2 } from 'lucide-react'
import toast from 'react-hot-toast'
import {
  useCreateVendorPaymentReleaseMutation,
  type VendorPayment,
} from '@/redux/service/vendor'

interface Props {
  open: boolean
  onClose: () => void
  projectId: number
  payment: VendorPayment | null
  /* 'markPaid' prefills the full remaining balance and a default note - the
     endpoint is identical either way, this is purely a UI convenience. */
  mode: 'release' | 'markPaid'
}

interface FormValues {
  amount_released: number | string
  description: string
}

const buildSchema = (balance: number) =>
  Yup.object({
    amount_released: Yup.number()
      .typeError('Must be a number')
      .required('Amount is required')
      .positive('Must be positive')
      .max(
        balance,
        `Cannot exceed the remaining balance ($${balance.toFixed(2)})`
      ),
    description: Yup.string().trim().required('Description is required'),
  })

const ReleasePaymentDialog = ({
  open,
  onClose,
  projectId,
  payment,
  mode,
}: Props) => {
  const [createRelease, { isLoading }] = useCreateVendorPaymentReleaseMutation()

  const balance = payment?.balance ?? 0
  const isMarkPaid = mode === 'markPaid'

  const initialValues: FormValues = {
    amount_released: isMarkPaid && balance > 0 ? balance : '',
    description: isMarkPaid ? 'Marked as paid in full' : '',
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className='max-h-[92vh] overflow-y-auto p-0 gap-0 max-w-md'>
        <DialogHeader className='px-6 pt-6 pb-4 border-b border-border'>
          <div className='flex items-center gap-3'>
            <div className='flex items-center justify-center w-8 h-8 rounded-lg bg-orange-500/10 border border-orange-500/20 shrink-0'>
              {isMarkPaid ? (
                <CheckCircle2 className='w-4 h-4 text-orange-500' />
              ) : (
                <Send className='w-4 h-4 text-orange-500' />
              )}
            </div>
            <div>
              <DialogTitle className='text-base font-semibold leading-tight'>
                {isMarkPaid ? 'Mark as Paid' : 'Release Payment'}
              </DialogTitle>
              <DialogDescription className='text-xs text-muted-foreground mt-0.5'>
                {isMarkPaid
                  ? 'Log the remaining balance as released in full.'
                  : 'Log a payment released against this milestone.'}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <Formik<FormValues>
          key={payment?.id ?? 'none'}
          initialValues={initialValues}
          enableReinitialize
          validationSchema={buildSchema(balance)}
          onSubmit={async (values, { resetForm }) => {
            if (!payment) return
            try {
              await createRelease({
                projectId,
                id: payment.id,
                amount_released: Number(values.amount_released),
                description: values.description,
              }).unwrap()
              toast.success(
                isMarkPaid ? 'Marked as paid' : 'Payment release logged'
              )
              resetForm()
              onClose()
            } catch (err: unknown) {
              const error = err as { data?: { message?: string } }
              toast.error(error?.data?.message ?? 'Failed to save')
            }
          }}
        >
          {({ handleSubmit, handleChange, values, errors, touched }) => (
            <div className='px-6 py-5 space-y-5'>
              <div className='flex items-center gap-2.5 px-3 py-2.5 rounded-lg bg-orange-500/5 border border-orange-500/20'>
                <Building2 className='w-4 h-4 text-orange-500 shrink-0' />
                <div className='min-w-0'>
                  <p className='text-[10px] font-semibold uppercase tracking-wide text-orange-500/70'>
                    Milestone
                  </p>
                  <p className='text-sm font-medium text-foreground truncate'>
                    {payment?.milestone_name ?? '—'}
                  </p>
                  <p className='text-[11px] text-muted-foreground mt-0.5'>
                    Remaining balance: ${balance.toFixed(2)}
                  </p>
                </div>
              </div>

              <div className='space-y-1.5'>
                <Label
                  htmlFor='vp-amount'
                  className='text-xs font-semibold uppercase tracking-wide text-muted-foreground'
                >
                  Amount Released ($){' '}
                  <span className='text-destructive'>*</span>
                </Label>
                <Input
                  id='vp-amount'
                  name='amount_released'
                  type='number'
                  min={0}
                  step='0.01'
                  placeholder='0.00'
                  value={values.amount_released}
                  onChange={handleChange}
                  className={cn(
                    'h-9 text-sm',
                    errors.amount_released && touched.amount_released
                      ? 'border-destructive focus-visible:ring-destructive/30'
                      : ''
                  )}
                />
                {errors.amount_released && touched.amount_released && (
                  <p className='text-xs text-destructive'>
                    {String(errors.amount_released)}
                  </p>
                )}
              </div>

              <div className='space-y-1.5'>
                <Label
                  htmlFor='vp-description'
                  className='text-xs font-semibold uppercase tracking-wide text-muted-foreground'
                >
                  Description <span className='text-destructive'>*</span>
                </Label>
                <Input
                  id='vp-description'
                  name='description'
                  placeholder='What is this release for?'
                  value={values.description}
                  onChange={handleChange}
                  className={cn(
                    'h-9 text-sm',
                    errors.description && touched.description
                      ? 'border-destructive focus-visible:ring-destructive/30'
                      : ''
                  )}
                />
                {errors.description && touched.description && (
                  <p className='text-xs text-destructive'>
                    {errors.description}
                  </p>
                )}
              </div>

              <div className='flex justify-end gap-2 pt-2 border-t border-border'>
                <Button
                  type='button'
                  variant='outline'
                  size='sm'
                  onClick={onClose}
                  className='h-8 text-xs border-border hover:bg-accent/30'
                >
                  Cancel
                </Button>
                <Button
                  size='sm'
                  onClick={() => handleSubmit()}
                  disabled={isLoading || !payment}
                  className='h-8 text-xs min-w-[100px] bg-orange-500 hover:bg-orange-600 text-white border-orange-500'
                >
                  {isLoading ? (
                    <>
                      <Loader2 className='w-3.5 h-3.5 mr-1.5 animate-spin' />
                      Saving…
                    </>
                  ) : isMarkPaid ? (
                    'Mark as Paid'
                  ) : (
                    'Release Payment'
                  )}
                </Button>
              </div>
            </div>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  )
}

export default ReleasePaymentDialog
